import { all, call, put, select, takeEvery, takeLatest, takeLeading } from "redux-saga/effects";
import { ActionWithPayload, PaginatedResponse, WithCallback } from "@shared/interfaces";
import { startLoading, stopLoading } from "@shared/store/actions";
// fix pass B2: the synchronous, non-redux read of the logged-in user this app already uses outside
// React state (ACL.tsx; tokenHandler.tsx's own header comment names "the shared saga" as a reader
// too) -- used below to re-dispatch the own-redemption summary on a failed createRedemptionRequest
// without needing `select`/AppState wiring into this saga file.
import { tokenHandler } from "@shared/utils";
import {
  AllTimeSummary,
  ApproveRedemptionRequestDto,
  ApproveRedemptionRequestSuccess,
  CreateRedemptionRequestDto,
  CreateRedemptionRequestSuccess,
  DenyRedemptionRequestDto,
  DenyRedemptionRequestSuccess,
  GetBadgeDefinitionsResponse,
  GetLeaderboardQuery,
  GetPendingRedemptionRequestsQuery,
  GetPendingRedemptionRequestsResponse,
  GetRedemptionLedgerQuery,
  GetRedemptionLedgerResponse,
  GetRewardItemsResponse,
  GivingActivity,
  // VALUES imported from this module here (everything else is a type, erased at build time):
  // getLeaderboardSaga's crowns guard compares against the real LEADERBOARD_BOARD member, and
  // getAllTimeRankSaga's fixed query literal needs both real enum members. saga.test.tsx virtual-mocks
  // this alias for that reason -- the same recipe reducer.tsx/reducer.test.tsx already use for their
  // own unavoidable alias value import.
  LEADERBOARD_BOARD,
  LEADERBOARD_PERIOD,
  LeaderboardCrownsResponse,
  LeaderboardResultsResponse,
  OrgRedemptionStats,
  Recognition,
  RedemptionRequestDecisionFailure,
  SparksSummary,
  SuggestedCollaboratorsResponse,
  User,
  UserBadgesResponse,
  UserRedemptionSummary,
} from "@shared/models";
// 923-core-context context/engineering/spark-rewards/phase-55-person-sparks-drawer.md §6: RECOGNITION_STATUS.APPROVED is a real VALUE read by
// getPersonSparksSaga below (the "recently recognised for" section is Approved-only, §5) -- same
// virtual-mock requirement under CRA's jest as the @shared/models VALUE imports above (only webpack
// resolves @shared/*).
import { RECOGNITION_STATUS } from "@shared/constants";

import {
  approveRedemptionRequest,
  clearRedemptionLedger,
  createRedemptionRequest,
  denyRedemptionRequest,
  getAllTimeRank,
  getAllTimeSummary,
  getBadgeDefinitions,
  getGivingActivity,
  getLeaderboard,
  getLeaderboardCollaborators,
  getLeaderboardCrowns,
  getOverviewLeaderboard,
  getOwnRedemptionSummary,
  getPendingRedemptionRequests,
  getPendingRedemptionRequestsBadgeCount,
  getPersonSparks,
  getProfileBadges,
  getRedemptionLedger,
  getRedemptionStats,
  getRewardItems,
  getSparksSummary,
  getUserBadges,
} from "./actions";
import {
  getPendingRedemptionRequestsLimit,
  getPersonSparksData,
  getPersonSparksUserId,
  getRedemptionLedgerLimit,
} from "./selectors";
import api from "../../api";
// Relative, not "@containers/User/api" -- only webpack resolves the "@containers/*" alias (same
// reasoning as the "@shared/*" VALUE imports above); a relative import needs no virtual mock and
// mirrors this file's own "../../api" precedent for its own container's api module.
import userApi from "../../../User/api";
// 923-core-context context/engineering/spark-rewards/phase-55-person-sparks-drawer.md §7 PR3: TYPE-only (erased at build time, so no virtual mock needed,
// unlike the userApi import above) -- refetchPersonSparksAfterConflictSaga below reads the open drawer's
// own combined snapshot off this shape.
import type { PersonSparks } from "../../interfaces";

// 923-core-context context/engineering/spark-rewards/phase-49-w3-pending-requests-panel.md: the panel's own fixed page size -- duplicated here (rather
// than imported from RedemptionsContainer.tsx) matching this repo's own convention of per-file literals
// over a shared constant for this exact feature (architecture doc's own "[decision] edit the seven
// literals; do not introduce a shared fixture"). Used ONLY as the fallback below, for the window before
// the panel has ever loaded once -- see refetchPendingRedemptionRequestsFirstPageSaga's own comment.
const PENDING_REQUESTS_DEFAULT_PAGE_SIZE = 20;

// review fix D: same reasoning as PENDING_REQUESTS_DEFAULT_PAGE_SIZE above -- the fallback
// refetchRedemptionLedgerFirstPageSaga below uses for the window before the ledger has ever loaded once
// and so has no persisted limit yet.
const REDEMPTION_LEDGER_DEFAULT_PAGE_SIZE = 20;

// try/catch/finally, matching Recognition's own saga shape (1.8) rather than Evaluation's dual
// stopLoading dispatch: a single stopLoading in `finally` cannot be forgotten on a new exit path.
export function* getSparksSummarySaga({ payload }: ActionWithPayload<number>) {
  try {
    yield put(startLoading());
    const response: SparksSummary = yield call(api.getSparksSummary, payload);
    yield put(getSparksSummary.success(response));
  } catch (error) {
    // `error` is already `unknown` (the catch clause's own type under strict mode) and the failure
    // creator's payload is `unknown` (D5) -- no cast needed, and `error as Error` would be a lie.
    yield put(getSparksSummary.failure(error));
  } finally {
    yield put(stopLoading());
  }
}

export function* getUserBadgesSaga({ payload }: ActionWithPayload<number>) {
  try {
    yield put(startLoading());
    const response: UserBadgesResponse = yield call(api.getUserBadges, payload);
    yield put(getUserBadges.success(response));
  } catch (error) {
    yield put(getUserBadges.failure(error));
  } finally {
    yield put(stopLoading());
  }
}

// D2 (923-core-context context/engineering/spark-rewards/phase-36-implementation-plan.md): no payload to pass through -- the catalog doesn't vary by
// user -- so unlike the two sagas above this one takes no `ActionWithPayload` argument at all.
export function* getBadgeDefinitionsSaga() {
  try {
    yield put(startLoading());
    const response: GetBadgeDefinitionsResponse = yield call(api.getBadgeDefinitions);
    // Unwraps `{ results, total }` to just `results` before dispatching success -- `total` isn't
    // used anywhere in this phase's UI scope (plan §2 PR1).
    yield put(getBadgeDefinitions.success(response.results));
  } catch (error) {
    yield put(getBadgeDefinitions.failure(error));
  } finally {
    yield put(stopLoading());
  }
}

// Phase 37 D3: mirrors getUserBadgesSaga's shape exactly (try/catch/finally, startLoading/
// stopLoading, an explicitly-annotated `call` result — an unannotated `yield` is `any` under this
// repo's strict TS) — it's the SAME wrapper (`api.getUserBadges`), called for a viewed profile's
// `userId` instead of the logged-in user's. `payload`, not a bare `userId` local, matching every
// other worker in this file. The success payload tags the response with the `userId` it was fetched
// for (D2) rather than dispatching `response.earned` alone.
export function* getProfileBadgesSaga({ payload }: ActionWithPayload<number>) {
  try {
    yield put(startLoading());
    const response: UserBadgesResponse = yield call(api.getUserBadges, payload);
    yield put(getProfileBadges.success({ userId: payload, badges: response.earned }));
  } catch (error) {
    yield put(getProfileBadges.failure(error));
  } finally {
    yield put(stopLoading());
  }
}

/**
 * overview-rebuild-pr5-plan.md: mirrors getBadgeDefinitionsSaga/getSparksSummarySaga's shape exactly
 * -- the api handler wraps its three reads in one transaction (recognition.controller.ts's own
 * comment), so there is nothing to combine here beyond dispatching the single response it returns.
 */
export function* getGivingActivitySaga({ payload }: ActionWithPayload<number>) {
  try {
    yield put(startLoading());
    const response: GivingActivity = yield call(api.getGivingActivity, payload);
    yield put(getGivingActivity.success(response));
  } catch (error) {
    yield put(getGivingActivity.failure(error));
  } finally {
    yield put(stopLoading());
  }
}

/**
 * 923-core-context context/engineering/spark-rewards/phase-49-w1-rewards-catalog.md: mirrors getBadgeDefinitionsSaga's shape exactly, including the
 * unwrap -- `put(getRewardItems.success(response.results))`, dropping `total`, which this phase's UI
 * scope has no use for.
 */
export function* getRewardItemsSaga() {
  try {
    yield put(startLoading());
    const response: GetRewardItemsResponse = yield call(api.getRewardItems);
    yield put(getRewardItems.success(response.results));
  } catch (error) {
    yield put(getRewardItems.failure(error));
  } finally {
    yield put(stopLoading());
  }
}

/**
 * Phase 38 D5: the ranked board's own worker -- registered on `getLeaderboard.request`, a DIFFERENT
 * action type from `getLeaderboardCrownsSaga` below, even though both call the same
 * `api.getLeaderboard`. Two separate `takeLatest` registrations (see `sparksSagas` below) so the two
 * fetches can never cancel each other.
 *
 * L2 (923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md): dispatches `{ board, period, results, viewer }` together,
 * not `response.results` alone -- the tab now renders its own pinned "You" row from `viewer`, and
 * gates BOTH on `board`/`period` matching its live toggles (see `getLeaderboard`'s own action comment
 * for the stale-board defect that gating exists to close). All four come off the one response, so the
 * dispatched payload is internally consistent by construction.
 *
 * 🚨 `response` is typed as the FULL discriminated union, not `LeaderboardResultsResponse` alone.
 * `GetLeaderboardQuery` (this worker's own payload type) still admits the CROWNS branch, and a crowns
 * response carries crown rows and NO `viewer` key at all. The ranked-shape annotation was ALREADY
 * dishonest before L2 -- it declared `results` as `LeaderboardEntry[]` where that branch returns
 * `LeaderboardCrown[]`; what L2 added was a second field to be wrong about, a `viewer` that would have
 * been `undefined` at runtime written into a field declared `LeaderboardViewer | null`. So this guard
 * closes a pre-existing hole that L2 widened, not one L2 opened. The branch is unreachable through
 * this tab's toggle
 * (`LeaderboardBoardToggle` never offers CROWNS), so the guard below is not expected to fire; it exists
 * so the annotation is TRUE rather than merely usually-true. A crowns response cannot populate a ranked
 * board, so it is reported as a failure -- never coerced into empty results, which would read on screen
 * as "nobody has scored" instead of "this did not work".
 */
// The text `errorHandlerMiddleware` (store.tsx) puts in its toast for the guard below. Deliberately
// the same wording as `LEADERBOARD_LOAD_FAILED_MESSAGE` (LeaderboardLoadStatus), which is what the
// tab's own inline banner renders on the same failure -- the viewer should not be told two different
// things about one event. Repeated as a literal rather than imported, because importing a component's
// copy into the store layer would couple them the wrong way round; same per-file-literal convention
// as PENDING_REQUESTS_DEFAULT_PAGE_SIZE at the top of this file.
const LEADERBOARD_UNEXPECTED_BOARD_MESSAGE = "We couldn't load the leaderboard. Refresh to try again.";

export function* getLeaderboardSaga({ payload }: ActionWithPayload<GetLeaderboardQuery>) {
  try {
    yield put(startLoading());
    const response: LeaderboardResultsResponse | LeaderboardCrownsResponse = yield call(api.getLeaderboard, payload);
    if (response.board === LEADERBOARD_BOARD.CROWNS) {
      yield put(getLeaderboard.failure(new Error(LEADERBOARD_UNEXPECTED_BOARD_MESSAGE)));
      return;
    }
    yield put(
      getLeaderboard.success({
        board: response.board,
        period: response.period,
        results: response.results,
        viewer: response.viewer,
        // L7 (923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md): rides along with the rest of this one response --
        // see getLeaderboard's own actions.tsx comment for why this is a widened Pick, not a second
        // fetch.
        window: response.window,
      }),
    );
  } catch (error) {
    yield put(getLeaderboard.failure(error));
  } finally {
    yield put(stopLoading());
  }
}

/**
 * 923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md L8: the "Your all-time" card's own rank -- a FIXED query
 * (`board: RECEIVED, period: ALL_TIME`), never the caller's current toggle selection, so this stays
 * pinned to ALL_TIME regardless of what `getLeaderboardSaga` above is fetching for the tab itself.
 * Mirrors that worker's own crowns guard (a `GetLeaderboardQuery` type still admits the CROWNS branch,
 * which carries no `viewer` at all) -- unreachable here since the query literal below never asks for
 * CROWNS, but kept so the response annotation stays true rather than merely usually-true, same
 * reasoning as getLeaderboardSaga's own comment.
 */
export function* getAllTimeRankSaga() {
  try {
    yield put(startLoading());
    const response: LeaderboardResultsResponse | LeaderboardCrownsResponse = yield call(api.getLeaderboard, {
      board: LEADERBOARD_BOARD.RECEIVED,
      period: LEADERBOARD_PERIOD.ALL_TIME,
    });
    if (response.board === LEADERBOARD_BOARD.CROWNS) {
      yield put(getAllTimeRank.failure(new Error(LEADERBOARD_UNEXPECTED_BOARD_MESSAGE)));
      return;
    }
    // `viewer` is `null` for a viewer with no score at all in ALL_TIME -- `?? null` here is not a
    // fallback for a missing field, it is narrowing `LeaderboardViewer | null` down to just its own
    // `rank`, preserving the `null` case rather than defaulting it to a fabricated 0.
    yield put(getAllTimeRank.success(response.viewer?.rank ?? null));
  } catch (error) {
    yield put(getAllTimeRank.failure(error));
  } finally {
    yield put(stopLoading());
  }
}

/**
 * 923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md L8: the same card's other two figures -- mirrors
 * getLeaderboardCollaboratorsSaga's shape exactly (no payload, no envelope to unwrap: the response IS
 * the success payload).
 */
export function* getAllTimeSummarySaga() {
  try {
    yield put(startLoading());
    const response: AllTimeSummary = yield call(api.getAllTimeSummary);
    yield put(getAllTimeSummary.success(response));
  } catch (error) {
    yield put(getAllTimeSummary.failure(error));
  } finally {
    yield put(stopLoading());
  }
}

/** Phase 38 D5: crowns' own independent worker -- see `getLeaderboardSaga`'s comment above. */
export function* getLeaderboardCrownsSaga({ payload }: ActionWithPayload<GetLeaderboardQuery>) {
  try {
    yield put(startLoading());
    const response: LeaderboardCrownsResponse = yield call(api.getLeaderboard, payload);
    yield put(getLeaderboardCrowns.success(response.results));
  } catch (error) {
    yield put(getLeaderboardCrowns.failure(error));
  } finally {
    yield put(stopLoading());
  }
}

/**
 * overview-leaderboard-card-plan.md Part 2: the Overview rail card's own worker -- calls the SAME
 * `api.getLeaderboard` as `getLeaderboardSaga` above, and (since L2, 923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md)
 * dispatches the same `{ results, viewer }` shape that worker now does too -- both screens render a
 * pinned "you" row from `viewer`. Kept as its own, separate triad rather than merged with
 * `getLeaderboardSaga` regardless: registered on its own `takeLatest` slot below, a DIFFERENT action
 * type from `getLeaderboard.request`, so this card's own board/period toggle can never cancel, or be
 * cancelled by, the tab's own fetch (D5's original independent-fetches reasoning, unchanged by L2).
 */
export function* getOverviewLeaderboardSaga({ payload }: ActionWithPayload<GetLeaderboardQuery>) {
  try {
    yield put(startLoading());
    const response: LeaderboardResultsResponse = yield call(api.getLeaderboard, payload);
    yield put(getOverviewLeaderboard.success({ results: response.results, viewer: response.viewer }));
  } catch (error) {
    yield put(getOverviewLeaderboard.failure(error));
  } finally {
    yield put(stopLoading());
  }
}

/**
 * redeem-tab-plan.md PR2: the Redeem tab's own read -- mirrors getSparksSummarySaga's shape exactly --
 * one call returns both the counters and the full ledger together, so there is nothing else to fetch
 * or combine here.
 */
export function* getOwnRedemptionSummarySaga({ payload }: ActionWithPayload<number>) {
  try {
    yield put(startLoading());
    const response: UserRedemptionSummary = yield call(api.getOwnRedemptionSummary, payload);
    yield put(getOwnRedemptionSummary.success(response));
  } catch (error) {
    yield put(getOwnRedemptionSummary.failure(error));
  } finally {
    yield put(stopLoading());
  }
}

/**
 * 923-core-context context/engineering/spark-rewards/phase-49-w2-create-request.md: mirrors createRecognitionSaga's cb-on-success shape (Recognition/
 * store/saga.tsx) -- `cb` fires only once the api call has actually succeeded, never on failure. The
 * container's own `cb` re-dispatches BOTH `getSparksSummary.request` and `getOwnRedemptionSummary.request`
 * (defect 1); this saga itself refetches nothing -- the caller's `cb` is the only refresh path.
 * The full `{ request, message }` response is dispatched as the success payload, not just
 * `response.request` -- see createRedemptionRequest's own actions.tsx comment for why.
 */
export function* createRedemptionRequestSaga({ payload }: ActionWithPayload<WithCallback<CreateRedemptionRequestDto>>) {
  try {
    const { cb, ...rest } = payload;
    yield put(startLoading());
    const response: CreateRedemptionRequestSuccess = yield call(api.createRedemptionRequest, rest);
    yield put(createRedemptionRequest.success(response));
    if (cb) {
      cb();
    }
  } catch (error) {
    yield put(createRedemptionRequest.failure(error));
    // fix pass B2: a network drop or timeout AFTER the POST has already committed server-side is
    // indistinguishable, from here, from a genuine rejection -- the api is holding the points either
    // way. Re-dispatching the own-redemption summary surfaces that as a Pending marker and the hold
    // line instead of a screen that reports failure while quietly holding a balance the user cannot
    // see. `cb` is deliberately NOT called here -- it stays the SUCCESS-only signal every other caller
    // depends on (this saga's own comment above); the error toast still fires off the `.failure`
    // dispatch on the line above exactly as before, this only adds a second, TRUE thing alongside it.
    // Degrades quietly when there is no cached user (e.g. a logout raced the request) rather than
    // refetching with a garbage id. The durable fix is an idempotency key on `POST
    // /redemption-requests` (A5) so a retried write cannot double-charge -- out of scope for this PR.
    const user = tokenHandler.getUser();
    if (user) {
      yield put(getOwnRedemptionSummary.request(Number(user.id)));
    }
  } finally {
    yield put(stopLoading());
  }
}

/**
 * 923-core-context context/engineering/spark-rewards/phase-49-w3-pending-requests-panel.md: the HR queue's own worker. Unlike every other worker in this
 * file, the success payload is NOT the bare api response -- it is tagged with `is_clear` (Chat's own
 * `getChatListSaga` precedent, saga.tsx:26 there) AND with the REQUEST's own `page`/`limit`, which the
 * reducer's `loadedPages`/`lastPageWasFull` rules both depend on (see reducer.tsx's own comment for the
 * three rules each must obey).
 */
export function* getPendingRedemptionRequestsSaga({ payload }: ActionWithPayload<GetPendingRedemptionRequestsQuery>) {
  try {
    yield put(startLoading());
    const response: GetPendingRedemptionRequestsResponse = yield call(api.getPendingRedemptionRequests, payload);
    yield put(
      getPendingRedemptionRequests.success({
        ...response,
        is_clear: !payload.page,
        page: payload.page,
        limit: payload.limit,
      }),
    );
  } catch (error) {
    yield put(getPendingRedemptionRequests.failure(error));
  } finally {
    yield put(stopLoading());
  }
}

/**
 * 923-core-context context/engineering/spark-rewards/phase-49-w5-org-wide-ledger.md: the org-wide ledger's own worker -- mirrors
 * getPendingRedemptionRequestsSaga's shape exactly, including the `is_clear`/`page`/`limit` tagging
 * (architecture §4.6 applied verbatim to this fourth pager). `payload` is passed straight through to
 * `api.getRedemptionLedger` unmodified -- the api wrapper is the only place that translates `page` to
 * the backend's 1-indexed form.
 */
export function* getRedemptionLedgerSaga({ payload }: ActionWithPayload<GetRedemptionLedgerQuery>) {
  try {
    yield put(startLoading());
    const response: GetRedemptionLedgerResponse = yield call(api.getRedemptionLedger, payload);
    yield put(
      getRedemptionLedger.success({
        ...response,
        is_clear: !payload.page,
        page: payload.page,
        limit: payload.limit,
      }),
    );
  } catch (error) {
    yield put(getRedemptionLedger.failure(error));
  } finally {
    yield put(stopLoading());
  }
}

/**
 * phase-50-w6b: the three org-wide stat cards' own worker -- mirrors getRewardItemsSaga's shape exactly,
 * except there is no `{ results, total }` envelope to unwrap: `GET /admin/redemptions/stats` returns the
 * four-field stats object directly, so the response IS the success payload.
 */
export function* getRedemptionStatsSaga() {
  try {
    yield put(startLoading());
    const response: OrgRedemptionStats = yield call(api.getRedemptionStats);
    yield put(getRedemptionStats.success(response));
  } catch (error) {
    yield put(getRedemptionStats.failure(error));
  } finally {
    yield put(stopLoading());
  }
}

/**
 * 923-core-context context/engineering/spark-rewards/phase-52-spread-your-sparks.md §4 S3: mirrors getRewardItemsSaga's shape exactly, including the
 * unwrap -- `put(getLeaderboardCollaborators.success(response.results))`, the established precedent
 * for this envelope shape (`{ results }`, no `total`).
 */
export function* getLeaderboardCollaboratorsSaga() {
  try {
    yield put(startLoading());
    const response: SuggestedCollaboratorsResponse = yield call(api.getLeaderboardCollaborators);
    yield put(getLeaderboardCollaborators.success(response.results));
  } catch (error) {
    yield put(getLeaderboardCollaborators.failure(error));
  } finally {
    yield put(stopLoading());
  }
}

/**
 * What the shared `request` client's response interceptor actually rejects with when the API answers
 * with an error -- `{ code, message }` (`api/app/middlewares/errorHandler.ts`'s wire shape; see
 * `request.tsx`'s `Promise.reject(data)` branch). Mirrors `Evaluation/store/saga.tsx`'s own
 * `isApiErrorBody` byte-for-byte -- same narrowing predicate, same reason: a network failure with no
 * response body rejects with the raw AxiosError instead, whose `code` is a STRING (e.g. "ERR_NETWORK"),
 * so a `code: number` cast would silently accept that value too.
 */
const isApiErrorBody = (error: unknown): error is { code: number; message: string } =>
  typeof (error as { code?: unknown })?.code === "number" &&
  typeof (error as { message?: unknown })?.message === "string";

const REDEMPTION_DECISION_CONFLICT_MESSAGE = "Someone else already decided this request.";
const REDEMPTION_DECISION_GENERIC_ERROR = "Something went wrong deciding this request. Please try again.";

/**
 * fix pass A/E: builds the failure payload every consumer downstream can safely render as a toast.
 * `error` itself is NEVER put on the payload -- `errorHandlerMiddleware` (store/store.tsx) renders
 * `payload.error || payload.message` as a raw React child, and the caught value here is an OBJECT (the
 * api's `{ code, message }` body, or a raw AxiosError on a network failure), which crashed the toast on
 * every failed decision before this fix, including the routine 409 race.
 *
 * fix pass E: a 409 is `REDEMPTION_REQUEST_NOT_PENDING` -- the api's NORMAL answer when another HR user
 * already decided this row, not an exceptional error -- so it gets its own friendlier message and
 * `isConflict: true`, which `repairAfterConflictSaga` below reads to trigger the one correct repair
 * (re-fetch page 0 so the stale row disappears), distinct from a generic failure that leaves the row in
 * place for a real retry.
 */
const toDecisionFailure = (id: number, error: unknown): RedemptionRequestDecisionFailure => {
  if (isApiErrorBody(error) && error.code === 409) {
    return { id, message: REDEMPTION_DECISION_CONFLICT_MESSAGE, isConflict: true };
  }
  return {
    id,
    message: isApiErrorBody(error) ? error.message : REDEMPTION_DECISION_GENERIC_ERROR,
    isConflict: false,
  };
};

/**
 * 923-core-context context/engineering/spark-rewards/phase-49-w4-approve-deny.md: approve's own worker, registered on `takeEvery` below -- per-row
 * decisions are independent (Recognition/store/saga.tsx:264-268's own reasoning for updateRecognition,
 * cited rather than re-derived: cancelling one row's task under takeLatest would leave a write that
 * already committed server-side silently unreflected in the queue). `.failure` is tagged with the
 * decided row's own `id` via `toDecisionFailure` above, not the bare `error` every other worker in this
 * file dispatches -- `takeEvery` runs one task per row, so the reducer needs to know WHICH row's
 * in-flight flag a given failure belongs to (actions.tsx's own comment on `RedemptionRequestDecisionFailure`).
 */
export function* approveRedemptionRequestSaga({
  payload,
}: ActionWithPayload<WithCallback<ApproveRedemptionRequestDto>>) {
  try {
    const { cb, id } = payload;
    yield put(startLoading());
    const response: ApproveRedemptionRequestSuccess = yield call(api.approveRedemptionRequest, id);
    yield put(approveRedemptionRequest.success(response));
    if (cb) {
      cb();
    }
  } catch (error) {
    yield put(approveRedemptionRequest.failure(toDecisionFailure(payload.id, error)));
  } finally {
    yield put(stopLoading());
  }
}

/**
 * Deny's own worker -- mirrors approveRedemptionRequestSaga exactly, differing only in the api call
 * (deny sends the required `reason` in its body, architecture §5.6).
 */
export function* denyRedemptionRequestSaga({ payload }: ActionWithPayload<WithCallback<DenyRedemptionRequestDto>>) {
  try {
    const { cb, id, reason } = payload;
    yield put(startLoading());
    const response: DenyRedemptionRequestSuccess = yield call(api.denyRedemptionRequest, { id, reason });
    yield put(denyRedemptionRequest.success(response));
    if (cb) {
      cb();
    }
  } catch (error) {
    yield put(denyRedemptionRequest.failure(toDecisionFailure(payload.id, error)));
  } finally {
    yield put(stopLoading());
  }
}

/**
 * fix pass G: the SINGLE owner of the page-0 refetch that restores `pendingRedemptionRequestsTotal`'s
 * authority after a decision (architecture §4.6 rule 4) -- previously duplicated in two container
 * callbacks (RedemptionsContainer's approve `cb`, DenyRedemptionRequestContainer's deny `cb`) while the
 * badge refresh for the SAME event already lived here as its own saga watcher
 * (`refreshPendingRedemptionRequestsBadgeCountSaga`, registered right below). Any future dispatcher of
 * approve/deny -- including W5's ledger -- now gets the refetch for free instead of having to remember
 * to wire it a third time.
 *
 * Reads the persisted limit via `select` rather than a module constant, matching §4.6 rule 5 (the
 * terminator's limit must be the one that produced the counter): a decision can only ever be made on a
 * row the panel has already rendered, so `pendingRedemptionRequestsLimit` is always a real, positive
 * value by the time this runs -- the module-level fallback only covers a theoretical call before the
 * panel's first load.
 *
 * fix pass E: also `call`ed directly by `repairAfterConflictSaga` below for a 409 -- same repair, same
 * limit, one implementation.
 *
 * ⚠️ Explicitly-accepted drift (architecture's own "the badge and panel counts drifting for up to 30s
 * after a pager fetch... self-heals on the next poll"): this refetch updates the PANEL's own total
 * immediately, but the TAB's badge count (a separate poll, `getPendingRedemptionRequestsBadgeCountSaga`)
 * only catches up on its own next 30s tick or the dedicated success-triggered refresh below. Both are
 * visible on screen at once and may briefly disagree -- accepted, not a defect.
 */
export function* refetchPendingRedemptionRequestsFirstPageSaga() {
  const persistedLimit: number = yield select(getPendingRedemptionRequestsLimit());
  yield put(
    getPendingRedemptionRequests.request({
      page: 0,
      limit: persistedLimit || PENDING_REQUESTS_DEFAULT_PAGE_SIZE,
    }),
  );
}

/**
 * review fix D: approving or denying a request writes a `redemptions` row (approve, architecture §5.5)
 * or moves the requester's `held` back to `redeemable` by arithmetic (deny) -- either way, the org-wide
 * ledger directly below the queue goes stale the moment a decision lands. Mirrors
 * `refetchPendingRedemptionRequestsFirstPageSaga` above: registered on the SAME two success types, a
 * SEPARATE `takeLatest` fork so one refetch failing can never suppress the other.
 *
 * Reads the persisted limit via `select` -- same §4.6 rule 5 reason as the pending-panel's own refetch.
 *
 * phase-50-w6a review fix B: `clearRedemptionLedger()` dispatched FIRST, unconditionally, before the
 * page-0 request -- mirrors RedemptionsContainer's own mount effect (previously the only site that
 * reset ahead of a request). Without it, a page-0 refetch that then FAILS leaves
 * `loadedPages`/`rows`/`total` at their PRE-decision values (`.failure` deliberately touches none of
 * them, §4.6 rule 3), so `retryRedemptionLedger` would re-request whatever page
 * `redemptionLedgerLoadedPages` was already sitting at instead of 0 -- the refresh the decision just
 * triggered silently lost, and de-dupe-by-id making the eventual re-append of the same rows a visual
 * no-op.
 */
export function* refetchRedemptionLedgerFirstPageSaga() {
  const persistedLimit: number = yield select(getRedemptionLedgerLimit());
  yield put(clearRedemptionLedger());
  yield put(
    getRedemptionLedger.request({
      page: 0,
      limit: persistedLimit || REDEMPTION_LEDGER_DEFAULT_PAGE_SIZE,
    }),
  );
}

/**
 * fix pass E: the 409 race's own repair -- `REDEMPTION_REQUEST_NOT_PENDING` means another HR user
 * decided this row first, so the queue's local copy is already stale. Re-requesting page 0 makes the
 * stale, still-clickable row disappear instead of staying in the list forever (every retry would just
 * 409 again). Registered on `takeEvery` below, not `takeLatest` -- a conflict on row A must not cancel
 * an in-flight repair for row B's own, independent conflict.
 */
export function* repairAfterConflictSaga({ payload }: ActionWithPayload<RedemptionRequestDecisionFailure>) {
  if (!payload.isConflict) {
    return;
  }
  yield call(refetchPendingRedemptionRequestsFirstPageSaga);
}

// 923-core-context context/engineering/spark-rewards/phase-49-w4-approve-deny.md: the tab badge's own background poll -- deliberately NO startLoading/
// stopLoading, mirroring Recognition/store/saga.tsx's own getPendingCountSaga precedent ("this is a
// background poll, and wrapping it would flash the app's global loader every 30s with nobody having
// asked for anything"). Calls the SAME api.getPendingRedemptionRequests the panel's own pager uses
// (there is no dedicated count endpoint, architecture §"The tab badge" / plan's own note) with
// `limit: 1`, but dispatches to this triad's OWN action -- never getPendingRedemptionRequests.success,
// which would route the response through the PANEL's reducer handler instead (see this action's own
// actions.tsx comment for exactly what that silently breaks).
export function* getPendingRedemptionRequestsBadgeCountSaga() {
  try {
    const response: GetPendingRedemptionRequestsResponse = yield call(api.getPendingRedemptionRequests, {
      page: 0,
      limit: 1,
    });
    yield put(getPendingRedemptionRequestsBadgeCount.success(response.total));
  } catch {
    // Bare .failure(), no payload -- mirrors getPendingCountSaga's own reasoning: a poll failing every
    // 30s (a dropped connection, an expired session) must not toast an error every 30s forever.
    yield put(getPendingRedemptionRequestsBadgeCount.failure());
  }
}

/**
 * 923-core-context context/engineering/spark-rewards/phase-49-w4-approve-deny.md: keeps the tab badge from disagreeing with the panel's own in-panel
 * badge after a decision (§"The two badges must not disagree on screen"). Mirrors Recognition/store/
 * saga.tsx's own refreshPendingCountSaga (D4) exactly -- re-dispatches rather than forking the fetch
 * worker directly, so every real fetch funnels through the ONE
 * takeLatest(getPendingRedemptionRequestsBadgeCount.request, ...) channel below. Safe only because
 * this worker is never suspended in a cancellable state (same reasoning, same citation).
 */
export function* refreshPendingRedemptionRequestsBadgeCountSaga() {
  yield put(getPendingRedemptionRequestsBadgeCount.request());
}

/**
 * 923-core-context context/engineering/spark-rewards/phase-55-person-sparks-drawer.md §6: the "a person's Sparks" drawer's own worker -- fetches FOUR
 * independent endpoints in PARALLEL via `all` (mirroring `getRecognitionCountsSaga`'s own precedent,
 * Recognition/store/saga.tsx:254-264: "Run in parallel via `all`, not two sequential `call`s, so a slow
 * [one] cannot delay [another]"), and combines them into one `PersonSparks` object. All four must
 * succeed -- if any one rejects, `all` rejects too, and NOTHING is dispatched but the tagged failure
 * (§6: "the drawer shows one error line, not a half-filled drawer").
 *
 * `status: RECOGNITION_STATUS.APPROVED` is sent EXPLICITLY on the recognitions call (§5: "HR is a
 * reviewer, and without it the list includes Review / Declined rows"). `limit: 3` matches the drawer's
 * own "latest 3" build-time call (§3). `response.rows` (not `.results`) -- `api.getRecognitions` returns
 * `PaginatedResponse<Recognition>` (`{ rows, count }`), a DIFFERENT envelope shape from the `{ results,
 * total }` this file's other list endpoints use.
 *
 * Deliberately NO `startLoading`/`stopLoading` (§6) -- the drawer renders its own inline loading state
 * (`personSparks.loading`, reducer.tsx), and the tab behind it must not flash the app's global loader
 * every time a ledger row is clicked.
 */
export function* getPersonSparksSaga({ payload }: ActionWithPayload<number>) {
  try {
    const [summary, badges, recognitions, user]: [
      UserRedemptionSummary,
      UserBadgesResponse,
      PaginatedResponse<Recognition>,
      User,
    ] = yield all([
      call(api.getUserRedemptionSummary, payload),
      call(api.getUserBadges, payload),
      call(api.getRecognitions, {
        page: 0,
        limit: 3,
        search: "",
        user_id: payload,
        status: RECOGNITION_STATUS.APPROVED,
      }),
      call(userApi.getUser, String(payload)),
    ]);
    yield put(
      getPersonSparks.success({
        userId: payload,
        data: {
          summary,
          flame: badges.flame,
          recentRecognitions: recognitions.rows,
          teamName: user.team?.name ?? null,
          firstName: user.first_name,
          lastName: user.last_name,
          avatarUrl: user.avatar_url ?? null,
        },
      }),
    );
  } catch (error) {
    yield put(getPersonSparks.failure({ userId: payload, error }));
  }
}

/**
 * 923-core-context context/engineering/spark-rewards/phase-55-person-sparks-drawer.md §6: "Refetch after a decision" -- approving or denying a redemption
 * request changes the counters (and, for approve, the ledger) of whichever person that request belonged
 * to. Registered on the SAME two success types `refetchPendingRedemptionRequestsFirstPageSaga`/
 * `refetchRedemptionLedgerFirstPageSaga` above already watch, as a SEPARATE fork -- one refetch failing
 * must not suppress another (this file's own established convention on those two).
 *
 * `takeEvery`, not `takeLatest`, unlike those siblings: their outcome is the same whichever action fired
 * (always page 0), but this one's depends on the action's own `request.user_id`. Under `takeLatest`, a
 * matching decision followed at once by a non-matching one would cancel the matching task before its
 * `put`, and the open drawer would silently miss its refetch. Today's modal backdrop makes that
 * unreachable, but that is the UI's guarantee, not this saga's.
 *
 * Reads the OPEN drawer's own `userId` via `select` rather than trusting the action alone: the decided
 * request's own `request.user_id` only matters if IT equals the drawer that is currently open. No open
 * drawer (`userId: null`) or a decision for a DIFFERENT person both do nothing -- there is nothing behind
 * a closed drawer to refresh, and refetching for the wrong person would show HR someone else's data
 * mid-review.
 */
export function* refetchPersonSparksAfterDecisionSaga({
  payload,
}: ActionWithPayload<ApproveRedemptionRequestSuccess | DenyRedemptionRequestSuccess>) {
  const openUserId: number | null = yield select(getPersonSparksUserId());
  if (openUserId !== null && payload.request.user_id === openUserId) {
    yield put(getPersonSparks.request(openUserId));
  }
}

/**
 * 923-core-context context/engineering/spark-rewards/phase-55-person-sparks-drawer.md §7 PR3, the 409 paragraph: `repairAfterConflictSaga` above already
 * repairs the QUEUE on a conflict, but it does not reach the drawer -- `refetchPersonSparksAfterDecisionSaga`
 * above listens to the two SUCCESS types only, and a decision FAILURE is tagged with the request's own
 * `id` (`RedemptionRequestDecisionFailure`), never a `user_id`. This is the drawer's own repair: on a
 * CONFLICT failure whose `id` is one of the OPEN drawer's own `summary.pendingRequests` ids, refetch that
 * person -- otherwise the stale, still-clickable card would sit in the drawer offering actions that would
 * just 409 again. A non-conflict failure does not refetch (the card's buttons simply re-enable, since the
 * shared in-flight set already drops the id on `.failure` either way).
 *
 * Reads BOTH the open drawer's `userId` and its `data` via `select` -- `userId` alone cannot answer "does
 * this id belong to that person", and a decision for someone with no drawer open, or for an id belonging
 * to a DIFFERENT open drawer's person entirely, must refetch nothing.
 */
export function* refetchPersonSparksAfterConflictSaga({
  payload,
}: ActionWithPayload<RedemptionRequestDecisionFailure>) {
  if (!payload.isConflict) {
    return;
  }
  const openUserId: number | null = yield select(getPersonSparksUserId());
  if (openUserId === null) {
    return;
  }
  const data: PersonSparks | null = yield select(getPersonSparksData());
  const belongsToOpenDrawer = data?.summary.pendingRequests.some((request) => request.id === payload.id) ?? false;
  if (belongsToOpenDrawer) {
    yield put(getPersonSparks.request(openUserId));
  }
}

function* sparksSagas() {
  yield takeLatest(getSparksSummary.request, getSparksSummarySaga);
  yield takeLatest(getUserBadges.request, getUserBadgesSaga);
  yield takeLatest(getBadgeDefinitions.request, getBadgeDefinitionsSaga);
  yield takeLatest(getRewardItems.request, getRewardItemsSaga);
  yield takeLatest(getGivingActivity.request, getGivingActivitySaga);
  yield takeLatest(getProfileBadges.request, getProfileBadgesSaga);
  yield takeLatest(getLeaderboard.request, getLeaderboardSaga);
  yield takeLatest(getLeaderboardCrowns.request, getLeaderboardCrownsSaga);
  yield takeLatest(getOverviewLeaderboard.request, getOverviewLeaderboardSaga);
  yield takeLatest(getOwnRedemptionSummary.request, getOwnRedemptionSummarySaga);
  yield takeLatest(getPendingRedemptionRequests.request, getPendingRedemptionRequestsSaga);
  // 923-core-context context/engineering/spark-rewards/phase-49-w5-org-wide-ledger.md: the org-wide ledger's own watcher, registered right after the HR
  // queue's -- takeLatest so a filter change (a new request dispatched before the previous one
  // resolves) cancels the stale in-flight fetch rather than letting it land after the new one.
  yield takeLatest(getRedemptionLedger.request, getRedemptionLedgerSaga);
  // phase-50-w6b: the three org-wide stat cards' own watcher -- org-wide and unpaginated, so takeLatest
  // (matching getRewardItems.request's own registration above) needs no further reasoning.
  yield takeLatest(getRedemptionStats.request, getRedemptionStatsSaga);
  // 923-core-context context/engineering/spark-rewards/phase-52-spread-your-sparks.md §4 S3: "Spread your Sparks" -- viewer-scoped and unpaginated, so
  // takeLatest needs no further reasoning, matching getRewardItems'/getRedemptionStats' own registration.
  yield takeLatest(getLeaderboardCollaborators.request, getLeaderboardCollaboratorsSaga);
  // 923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md L8: own takeLatest slots, independent of getLeaderboard's own --
  // see getAllTimeRank's own actions.tsx comment for why this must never share a type with the tab's
  // own toggle-driven fetch.
  yield takeLatest(getAllTimeRank.request, getAllTimeRankSaga);
  yield takeLatest(getAllTimeSummary.request, getAllTimeSummarySaga);
  yield takeLatest(getPendingRedemptionRequestsBadgeCount.request, getPendingRedemptionRequestsBadgeCountSaga);
  // Re-dispatches the badge-count request above on every successful decision -- see
  // refreshPendingRedemptionRequestsBadgeCountSaga's own comment.
  yield takeLatest(
    [approveRedemptionRequest.success, denyRedemptionRequest.success],
    refreshPendingRedemptionRequestsBadgeCountSaga,
  );
  // fix pass G: the single owner of the page-0 refetch that restores the panel's own `total` after a
  // decision -- see refetchPendingRedemptionRequestsFirstPageSaga's own comment. A SEPARATE watcher from
  // the badge refresh above (registered on the SAME two action types) rather than one worker doing both
  // -- redux-saga forks each `takeLatest` independently, so one refetch failing can never suppress the
  // other's dispatch.
  yield takeLatest(
    [approveRedemptionRequest.success, denyRedemptionRequest.success],
    refetchPendingRedemptionRequestsFirstPageSaga,
  );
  // review fix D: a THIRD watcher on the SAME two success types -- refreshes the org-wide ledger below
  // the queue, which a decision otherwise leaves stale. A separate fork from the two above, for the
  // same "one refetch failing must not suppress another" reasoning.
  yield takeLatest(
    [approveRedemptionRequest.success, denyRedemptionRequest.success],
    refetchRedemptionLedgerFirstPageSaga,
  );
  // takeLeading, not takeLatest -- two rapid clicks (before React re-renders the disabled button) would
  // CANCEL the first task under takeLatest while its POST is already in flight and has already
  // committed server-side -- the cancelled task never dispatches `.success`, so `cb` never fires for
  // it, but the row is written all the same. Net effect: two holds for one reported success, with no
  // delete/undo. takeLeading runs the FIRST request and drops every subsequent one until it completes,
  // which is exactly what a fire-and-forget, non-idempotent write needs. Same choice, and the same
  // reasoning, as recognitionSagas' bulkApproveRecognitions registration (Recognition/store/
  // saga.tsx:218-227). The container's own busy state ("RedeemCreateRequest" loader ANDed with the
  // clicked card's id) is the accidental-double-click AFFORDANCE; this registration is the
  // ONE-AT-A-TIME guarantee underneath it (corrected: NOT a "write-once" guarantee -- two clicks
  // separated by a completed round trip deliberately produce two requests, §0.3 allows multiple pending
  // requests for the same item) -- neither substitutes for the other.
  //
  // 🚨 fix pass H7, for W4: do NOT copy this shape for approve/deny. Those run on `takeEvery`
  // (multiple rows reviewed independently, several writes genuinely in flight at once), so a single
  // `clickedId` cannot discriminate which row a given write belongs to the way it does here where
  // `takeLeading` guarantees at most one write in flight -- W4 needs a SET of in-flight row ids, not
  // one scalar.
  // 923-core-context context/engineering/spark-rewards/phase-49-w5-org-wide-ledger.md: this is now the sole write-once triad in this slice --
  // createRedemption's own takeLeading registration (the HR-gated mark-consumed write) was deleted
  // alongside that flow.
  yield takeLeading(createRedemptionRequest.request, createRedemptionRequestSaga);
  // 923-core-context context/engineering/spark-rewards/phase-49-w4-approve-deny.md: takeEvery, NOT takeLatest or takeLeading -- approve/deny are
  // independent per-row mutations (Recognition/store/saga.tsx:264-268's own reasoning for
  // updateRecognition, cited above on each worker). Two rows decided in quick succession must BOTH
  // complete; takeLatest would cancel the first task while its write is already committing
  // server-side, and takeLeading would silently drop the second row's decision entirely.
  yield takeEvery(approveRedemptionRequest.request, approveRedemptionRequestSaga);
  yield takeEvery(denyRedemptionRequest.request, denyRedemptionRequestSaga);
  // fix pass E: the 409 race's own repair, registered on takeEvery for the same reason as the two
  // decisions above -- a conflict on one row must not cancel an in-flight repair for another.
  yield takeEvery([approveRedemptionRequest.failure, denyRedemptionRequest.failure], repairAfterConflictSaga);
  // 923-core-context context/engineering/spark-rewards/phase-55-person-sparks-drawer.md §6: the drawer's own fetch -- takeLatest, mirroring every other
  // single-subject read in this file (e.g. getOwnRedemptionSummary above); opening a new person's
  // drawer before the previous one's fetch resolves should cancel it, not race it.
  yield takeLatest(getPersonSparks.request, getPersonSparksSaga);
  // §6 "Refetch after a decision" -- a FOURTH watcher on the same two success types the three
  // refetch/refresh sagas above already watch, its own independent fork (see
  // refetchPersonSparksAfterDecisionSaga's own comment for why, including why takeEvery).
  yield takeEvery(
    [approveRedemptionRequest.success, denyRedemptionRequest.success],
    refetchPersonSparksAfterDecisionSaga,
  );
  // 923-core-context context/engineering/spark-rewards/phase-55-person-sparks-drawer.md §7 PR3, the 409 paragraph: the drawer's own conflict repair,
  // registered on the FAILURE types (not the SUCCESS ones the three watchers above share) -- see the
  // saga's own comment for why repairAfterConflictSaga cannot cover this case.
  yield takeEvery(
    [approveRedemptionRequest.failure, denyRedemptionRequest.failure],
    refetchPersonSparksAfterConflictSaga,
  );
}

export default sparksSagas;
